/**
 * Launcher V2 — embedded-image masking (U3a). Promoted, byte-for-byte
 * approach, from `explorations/launcher-copy-generation/body-sanitize.ts`
 * (kept as provenance) — discovered necessary by the real Gong run:
 *
 * Real Gong Engage step bodies contain embedded base64 screenshots
 * (`<img src="data:image/png;base64,…">`) that run ~229,000 characters PER
 * STEP (~60k tokens). Sent raw, a 6-step flow is ~300k input tokens, and the
 * model cannot echo the base64 back inside any reasonable `max_tokens`, so
 * the answer truncates → schema-invalid → 502. The image is not copy; the
 * model has no business seeing or reproducing it.
 *
 * `maskEmbeddedImages` swaps every data-URI image tag for a short
 * placeholder (`[[IMAGE_1]]`, `[[IMAGE_2]]`, …) the prompt tells the model to
 * keep verbatim, and returns a `restore` that puts the original tags back
 * into the model's output. `maskForDisplay`/`restoreFromDisplay` are the
 * browser-facing counterpart (U7a): a human-readable `"[image n: N KB]"`
 * marker instead of an opaque placeholder, for showing a diff without
 * rendering (or re-uploading) the actual screenshot bytes.
 *
 * ISOMORPHIC — no server imports. Both `copy-generation.ts` (server) and the
 * U7a review drawer (browser) import this file.
 */

const DATA_URI_IMG = /<img\b[^>]*\bsrc\s*=\s*["']data:image\/[a-z+.-]+;base64,[^"']*["'][^>]*>/gi;

export interface MaskRestoreResult {
  bodies: string[];
  /** `[[IMAGE_n]]` placeholders whose real tag never came back in the output. */
  unrestoredPlaceholders: string[];
  /** `[[IMAGE_n]]` placeholders present at mask time that never appeared in the output. */
  droppedImages: string[];
  /**
   * D8 — `[[IMAGE_n]]` placeholders that appear MORE THAN ONCE across the
   * output (whether both occurrences are in the placeholder's own origin
   * body or not — see {@link misplacedPlaceholders} for the separate,
   * independent check on WHICH body an occurrence landed in).
   */
  duplicatedPlaceholders: string[];
  /**
   * D8 — `[[IMAGE_n]]` placeholders that appear in a DIFFERENT body than the
   * one they were masked out of. Independent of {@link duplicatedPlaceholders}:
   * a placeholder relocated to exactly one wrong body is misplaced but not
   * duplicated; one repeated once in its own body and once elsewhere is both.
   */
  misplacedPlaceholders: string[];
}

export interface MaskedBodies {
  /** Bodies with every data-URI `<img>` replaced by `[[IMAGE_n]]`. */
  bodies: string[];
  /** How many images were masked across all bodies. */
  imageCount: number;
  /** Total characters removed — the size of the problem, for the findings doc. */
  charsRemoved: number;
  /** Put the real tags back into model output. Unknown placeholders are left in place and reported. */
  restore: (bodies: readonly string[]) => MaskRestoreResult;
}

/**
 * Masks every embedded data-URI `<img>` across `bodies`, numbered in
 * encounter order (across ALL bodies, not per-body — a body-local counter
 * would collide when restoring a reordered/merged set of bodies).
 */
export function maskEmbeddedImages(bodies: readonly string[]): MaskedBodies {
  const images = new Map<string, { tag: string; bodyIndex: number }>();
  let charsRemoved = 0;
  const masked = bodies.map((body, bodyIndex) =>
    body.replace(DATA_URI_IMG, (tag) => {
      const placeholder = `[[IMAGE_${images.size + 1}]]`;
      images.set(placeholder, { tag, bodyIndex });
      charsRemoved += tag.length - placeholder.length;
      return placeholder;
    }),
  );
  return {
    bodies: masked,
    imageCount: images.size,
    charsRemoved,
    restore(out) {
      // D8 — every OCCURRENCE of a known placeholder, keyed by placeholder,
      // recording which output body index it was found in. More than one
      // entry total → duplicated; any entry whose body index differs from
      // the placeholder's own origin body index → misplaced. The two checks
      // are independent (see `MaskRestoreResult`'s own doc).
      const occurrences = new Map<string, number[]>();
      const unrestored: string[] = [];
      const restored = out.map((body, outIndex) =>
        body.replace(/\[\[IMAGE_\d+\]\]/g, (placeholder) => {
          const entry = images.get(placeholder);
          if (entry === undefined) {
            unrestored.push(placeholder);
            return placeholder;
          }
          const seenAt = occurrences.get(placeholder) ?? [];
          seenAt.push(outIndex);
          occurrences.set(placeholder, seenAt);
          return entry.tag;
        }),
      );
      const duplicatedPlaceholders: string[] = [];
      const misplacedPlaceholders: string[] = [];
      for (const [placeholder, seenAt] of occurrences) {
        if (seenAt.length > 1) duplicatedPlaceholders.push(placeholder);
        const originBodyIndex = images.get(placeholder)?.bodyIndex;
        if (seenAt.some((bodyIndex) => bodyIndex !== originBodyIndex)) misplacedPlaceholders.push(placeholder);
      }
      return {
        bodies: restored,
        unrestoredPlaceholders: [...new Set(unrestored)],
        droppedImages: [...images.keys()].filter((placeholder) => !occurrences.has(placeholder)),
        duplicatedPlaceholders,
        misplacedPlaceholders,
      };
    },
  };
}

/**
 * Approximate on-the-wire KB size of a base64 `<img>` tag's payload (base64
 * inflates bytes by ~4/3, so decoded size ≈ `base64Length * 3/4`, adjusted
 * for `=` padding).
 */
export function imageSizeKb(tag: string): number {
  const match = /base64,([^"']*)["']/i.exec(tag);
  if (!match) return 0;
  const base64 = match[1];
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  const bytes = Math.floor((base64.length * 3) / 4) - padding;
  return Math.round(bytes / 1024);
}

export type ImageDisplayMap = Record<string, string>;

/**
 * Browser-facing counterpart to `maskEmbeddedImages`: replaces every embedded
 * image with a human-readable `"[image n: N KB]"` marker (rather than an
 * opaque `[[IMAGE_n]]` placeholder) — for showing a copy diff without
 * rendering or re-transmitting the actual screenshot bytes. `map` keys the
 * marker string back to its real `<img>` tag for `restoreFromDisplay`.
 */
export function maskForDisplay(html: string): { html: string; map: ImageDisplayMap } {
  const map: ImageDisplayMap = {};
  let count = 0;
  const masked = html.replace(DATA_URI_IMG, (tag) => {
    count += 1;
    const marker = `[image ${count}: ${imageSizeKb(tag)} KB]`;
    map[marker] = tag;
    return marker;
  });
  return { html: masked, map };
}

export interface RestoreFromDisplayResult {
  /** Byte-exact only when `dropped` and `duplicated` are both empty. */
  html: string;
  /** 1-based image indices (the marker's own `n`) whose marker no longer appears anywhere in `html` — the rep's edit deleted it. */
  dropped: number[];
  /** 1-based image indices whose marker appears MORE THAN ONCE in `html` — the rep's edit (or a paste) duplicated it. */
  duplicated: number[];
}

/** Parses the `n` out of a `"[image n: N KB]"` marker string ({@link maskForDisplay}'s own format). */
const DISPLAY_MARKER_INDEX = /^\[image (\d+): \d+ KB\]$/;

/**
 * Reverses `maskForDisplay` — byte-exact when every marker in `map` appears
 * in `html` EXACTLY once (D8). `StepReviewCard`'s contentEditable body is
 * rep-editable, so either violation is a real, reachable case (not just a
 * defensive check): a rep who selects across a marker and deletes it drops
 * an image; one who copies a paragraph containing a marker duplicates it.
 * Both are reported by 1-based image index (`n`) rather than the raw marker
 * string — that index is what the UI's warning chip ("Image n removed" /
 * "Image n duplicated", D8) and the tally footer show the rep.
 */
export function restoreFromDisplay(html: string, map: ImageDisplayMap): RestoreFromDisplayResult {
  let out = html;
  const dropped: number[] = [];
  const duplicated: number[] = [];
  for (const [marker, tag] of Object.entries(map)) {
    const match = DISPLAY_MARKER_INDEX.exec(marker);
    const index = match ? Number(match[1]) : null;
    const occurrences = out.split(marker).length - 1;
    if (index !== null) {
      if (occurrences === 0) dropped.push(index);
      if (occurrences > 1) duplicated.push(index);
    }
    out = out.split(marker).join(tag);
  }
  return { html: out, dropped: dropped.sort((a, b) => a - b), duplicated: duplicated.sort((a, b) => a - b) };
}
